import { useState, useEffect, useRef, useCallback } from 'react';
import { GEOFENCE_CONFIG } from '../config/constants';

// Haversine distance in meters
function getDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function useGeofenceDetection(pickupLat, pickupLng, enabled = true) {
  const [isInsideGeofence, setIsInsideGeofence] = useState(false);
  const [hasArrived, setHasArrived] = useState(false);
  const [distanceToPickup, setDistanceToPickup] = useState(null);
  const [currentPosition, setCurrentPosition] = useState(null);
  const graceTimerRef = useRef(null);
  const watchIdRef = useRef(null);
  const onArriveRef = useRef(null);
  const onLeaveRef = useRef(null);
  const onReturnRef = useRef(null);

  const setCallbacks = useCallback(({ onArrive, onLeave, onReturn }) => {
    onArriveRef.current = onArrive;
    onLeaveRef.current = onLeave;
    onReturnRef.current = onReturn;
  }, []);

  useEffect(() => {
    if (!enabled || !pickupLat || !pickupLng || !navigator.geolocation) return;

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setCurrentPosition({ lat: latitude, lng: longitude });

        const distance = getDistance(latitude, longitude, pickupLat, pickupLng);
        setDistanceToPickup(Math.round(distance));

        const inside = distance <= GEOFENCE_CONFIG.ARRIVAL_RADIUS_METERS;

        if (inside) {
          if (graceTimerRef.current) {
            clearTimeout(graceTimerRef.current);
            graceTimerRef.current = null;
          }

          if (!isInsideGeofence) {
            setIsInsideGeofence(true);
            if (!hasArrived) {
              setHasArrived(true);
              onArriveRef.current?.();
            } else {
              onReturnRef.current?.();
            }
          }
        } else {
          if (isInsideGeofence && !graceTimerRef.current) {
            graceTimerRef.current = setTimeout(() => {
              setIsInsideGeofence(false);
              onLeaveRef.current?.();
              graceTimerRef.current = null;
            }, GEOFENCE_CONFIG.GPS_DRIFT_GRACE_SECONDS * 1000);
          }
        }
      },
      (error) => console.error('Geolocation error:', error),
      { enableHighAccuracy: true, maximumAge: 3000, timeout: 10000 }
    );

    return () => {
      if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
      if (graceTimerRef.current) clearTimeout(graceTimerRef.current);
    };
  }, [pickupLat, pickupLng, enabled, hasArrived, isInsideGeofence]);

  return { isInsideGeofence, hasArrived, distanceToPickup, currentPosition, setCallbacks };
}
